/**
 * DragSelectionManager - Manages drag-to-queue functionality
 * Encapsulates drag state and provides cleanup to prevent memory leaks
 */
import { buildInputProof } from "./input_intent_tracker.js";

class DragSelectionManager {
    constructor() {
        // Drag state
        this.isDragging = false;
        this.dragStartTile = null;
        this.draggedTiles = new Set();
        this.dragPreviewElement = null;
        this.dragStartTimeout = null;
        this.touchStartTime = 0;
        this.touchStartPosition = { x: 0, y: 0 };
        this.dragGeometryCache = null;
        this.activeDragSelectionElements = [];
        this.pendingDragSelectionFrame = null;
        this.latestDragPointerCoords = null;

        // Event listener references for cleanup
        this.mouseMoveHandler = null;
        this.touchStartHandler = null;
        this.touchMoveHandler = null;
        this.mouseUpHandler = null;
        this.touchEndHandler = null;
        this.touchCancelHandler = null;
        this.keyDownHandler = null;
        this.turboLoadHandler = null;
        this.turboFrameLoadHandler = null;
        this.turboBeforeCacheHandler = null;

        // Setup flag
        this._setup = false;
        this._initialized = false;

        // Constants
        this.Y_POSITION_CLOSE_THRESHOLD = 10; // pixels
    }

    /**
     * Find the mining grid element (not other Tailwind 'grid' layouts).
     * The mining grid is the one that carries queue-related data attributes.
     */
    getMiningGridElement() {
        const scoped = document.querySelector('#grid-panel .grid[data-queue-mode]');
        if (scoped) return scoped;
        return document.querySelector('.grid[data-queue-mode]');
    }

    /**
     * Get coordinates from mouse or touch event
     */
    getEventCoordinates(event) {
        if (event.touches && event.touches.length > 0) {
            return { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }
        if (event.changedTouches && event.changedTouches.length > 0) {
            return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
        }
        return { x: event.clientX, y: event.clientY };
    }

    /**
     * Clear active drag selection visual indicators
     */
    clearActiveDragSelections() {
        if (this.activeDragSelectionElements.length === 0) {
            return;
        }

        this.activeDragSelectionElements.forEach(el => {
            if (el && el.classList) {
                el.classList.remove('drag-selected');
            }
        });
        this.activeDragSelectionElements = [];
    }

    /**
     * Invalidate drag geometry cache
     */
    invalidateDragGeometryCache() {
        this.dragGeometryCache = null;
    }

    /**
     * Prime drag geometry cache with tile positions
     */
    primeDragGeometryCache() {
        if (!this.dragStartTile) {
            return;
        }

        const grid = this.getMiningGridElement();
        const tiles = document.querySelectorAll('[data-controller*="tile"]');

        const startRect = this.dragStartTile.getBoundingClientRect();
        const gridRect = grid ? grid.getBoundingClientRect() : null;
        const maxQueueSize = grid ? (parseInt(grid.getAttribute('data-max-queue-size'), 10) || 0) : 0;

        const cachedTiles = [];
        tiles.forEach(tile => {
            if (!tile) {
                return;
            }

            const tileIdAttr = tile.getAttribute('data-tile-tile-id-value');
            if (!tileIdAttr || tileIdAttr === '0') {
                return;
            }

            const rect = tile.getBoundingClientRect();
            cachedTiles.push({
                tile,
                tileId: parseInt(tileIdAttr, 10),
                centerX: rect.left + rect.width / 2,
                centerY: rect.top + rect.height / 2
            });
        });

        this.dragGeometryCache = {
            startRect,
            gridRect,
            maxQueueSize,
            tiles: cachedTiles,
            scrollX: typeof window !== 'undefined' ? window.scrollX : 0,
            scrollY: typeof window !== 'undefined' ? window.scrollY : 0
        };
    }

    /**
     * Schedule drag selection update using requestAnimationFrame
     */
    scheduleDragSelectionUpdate(coords) {
        this.latestDragPointerCoords = coords;

        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
            this.updateDragSelection(this.latestDragPointerCoords);
            return;
        }

        if (this.pendingDragSelectionFrame) {
            return;
        }

        this.pendingDragSelectionFrame = window.requestAnimationFrame(() => {
            this.pendingDragSelectionFrame = null;

            if (!this.isDragging || !this.dragStartTile || !this.latestDragPointerCoords) {
                return;
            }

            this.updateDragSelection(this.latestDragPointerCoords);
        });
    }

    /**
     * Cancel pending drag selection update
     */
    cancelPendingDragSelectionUpdate() {
        if (this.pendingDragSelectionFrame &&
            typeof window !== 'undefined' &&
            typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(this.pendingDragSelectionFrame);
        }

        this.pendingDragSelectionFrame = null;
        this.latestDragPointerCoords = null;
    }

    /**
     * Sort tiles by drag direction
     */
    sortTilesByDragDirection(tiles, startRect, currentX, currentY) {
        if (tiles.length === 0) return tiles;

        const startX = startRect.left + startRect.width / 2;
        const startY = startRect.top + startRect.height / 2;

        const dragDirectionX = currentX - startX;
        const dragDirectionY = currentY - startY;

        return [...tiles].sort((a, b) => {
            let yComparison;
            if (dragDirectionY >= 0) {
                yComparison = a.centerY - b.centerY;
            } else {
                yComparison = b.centerY - a.centerY;
            }

            if (Math.abs(yComparison) < this.Y_POSITION_CLOSE_THRESHOLD) {
                if (dragDirectionX >= 0) {
                    return a.centerX - b.centerX;
                } else {
                    return b.centerX - a.centerX;
                }
            }

            return yComparison;
        });
    }

    /**
     * Update drag selection based on current pointer position
     */
    updateDragSelection(coords) {
        if (!this.dragStartTile || !this.isDragging || !coords) {
            return;
        }

        if (this.dragGeometryCache &&
            typeof window !== 'undefined' &&
            (this.dragGeometryCache.scrollX !== window.scrollX || this.dragGeometryCache.scrollY !== window.scrollY)) {
            this.invalidateDragGeometryCache();
        }

        if (!this.dragGeometryCache) {
            this.primeDragGeometryCache();
        }

        if (!this.dragGeometryCache || !this.dragGeometryCache.startRect) {
            return;
        }

        const { tiles: cachedTiles, maxQueueSize, gridRect, startRect } = this.dragGeometryCache;
        const currentX = coords.x;
        const currentY = coords.y;

        this.clearActiveDragSelections();
        this.draggedTiles.clear();

        let minX = Math.min(startRect.left, currentX);
        let maxX = Math.max(startRect.left + startRect.width, currentX);
        let minY = Math.min(startRect.top, currentY);
        let maxY = Math.max(startRect.top + startRect.height, currentY);

        if (gridRect) {
            minX = Math.max(minX, gridRect.left);
            maxX = Math.min(maxX, gridRect.right);
            minY = Math.max(minY, gridRect.top);
            maxY = Math.min(maxY, gridRect.bottom);
        }

        const validTiles = [];

        cachedTiles.forEach(({ tile, tileId, centerX, centerY }) => {
            if (!tile || !tile.isConnected) {
                return;
            }

            if (centerX >= minX && centerX <= maxX && centerY >= minY && centerY <= maxY) {
                const progressBar = tile.querySelector('.progress-bar');
                const isDefeated = progressBar && progressBar.style.display === 'none';

                const tileInner = tile.querySelector(`#tile_inner_${tileId}`);
                const isQueued = tileInner && tileInner.classList.contains('you-are-queued-here');

                if (!isDefeated && !isQueued) {
                    validTiles.push({
                        tile,
                        tileId,
                        centerX,
                        centerY
                    });
                }
            }
        });

        const sortedValidTiles = this.sortTilesByDragDirection(validTiles, startRect, currentX, currentY);
        const queueLimit = maxQueueSize || 0;
        const tilesToSelect = queueLimit > 0 ? sortedValidTiles.slice(0, queueLimit) : [];
        const tilesToQueue = queueLimit > 0 ? sortedValidTiles.slice(0, queueLimit) : [];

        tilesToSelect.forEach(({ tile }) => {
            tile.classList.add('drag-selected');
            this.activeDragSelectionElements.push(tile);
        });

        tilesToQueue.forEach(({ tileId }) => {
            this.draggedTiles.add(tileId);
        });

        const limitMessage = `${tilesToSelect.length} tiles`;
        this.updateDragPreview(minX, minY, maxX - minX, maxY - minY, tilesToSelect.length, limitMessage);
    }

    /**
     * Update drag preview element
     */
    updateDragPreview(x, y, width, height, tileCount, limitMessage = null) {
        if (!this.dragPreviewElement) {
            this.dragPreviewElement = document.createElement('div');
            this.dragPreviewElement.className = 'drag-preview';
            document.body.appendChild(this.dragPreviewElement);
        }

        this.dragPreviewElement.style.left = x + 'px';
        this.dragPreviewElement.style.top = y + 'px';
        this.dragPreviewElement.style.width = width + 'px';
        this.dragPreviewElement.style.height = height + 'px';
        this.dragPreviewElement.textContent = limitMessage || `${tileCount} tiles`;
        this.dragPreviewElement.style.display = 'block';
    }

    /**
     * Finalize drag selection and queue tiles
     */
    finalizeDragSelection(event) {
        if (this.dragStartTimeout) {
            clearTimeout(this.dragStartTimeout);
            this.dragStartTimeout = null;
        }

        const wasActuallyDragging = this.isDragging && this.dragStartTile && this.draggedTiles.size > 0;
        const startPosition = this.touchStartPosition || { x: 0, y: 0 };
        const endCoords = this.latestDragPointerCoords || this.getEventCoordinates(event || {});
        const dragDistance = Math.sqrt(
            Math.pow((endCoords.x || 0) - (startPosition.x || 0), 2) +
            Math.pow((endCoords.y || 0) - (startPosition.y || 0), 2)
        );
        const dragDuration = Date.now() - this.touchStartTime;
        const inputProof = buildInputProof(event, {
            kind: "drag",
            durationMs: dragDuration,
            distancePx: dragDistance
        });

        this.cancelPendingDragSelectionUpdate();

        this.isDragging = false;
        this.dragStartTile = null;
        this.invalidateDragGeometryCache();
        this.clearActiveDragSelections();

        if (this.dragPreviewElement) {
            this.dragPreviewElement.style.display = 'none';
        }

        if (wasActuallyDragging && this.isQueueModeEnabled()) {
            this.queueMultipleTiles(Array.from(this.draggedTiles), inputProof);
        }

        this.draggedTiles.clear();
    }

    /**
     * Check if queue mode is enabled
     */
    isQueueModeEnabled() {
        const grid = this.getMiningGridElement();
        const queueMode = grid ? grid.getAttribute('data-queue-mode') : null;
        const enabled = queueMode === 'sequential' || queueMode === 'random';

        if (!grid) return false;
        return enabled;
    }

    /**
     * Queue multiple tiles via WebSocket
     */
    queueMultipleTiles(tileIds, inputProof = null) {
        if (!window.WorkerChannel) {
            console.warn("WorkerChannel not available for batch queueing");
            return;
        }

        WorkerChannel.performAction("drag_dig", {
            tile_ids: tileIds,
            input_proof: inputProof
        });
    }

    /**
     * Start drag operation
     */
    startDrag(tileElement, coords) {
        if (this.dragStartTimeout) {
            clearTimeout(this.dragStartTimeout);
            this.dragStartTimeout = null;
        }

        this.isDragging = false;
        this.dragStartTile = tileElement;
        this.invalidateDragGeometryCache();
        this.draggedTiles.clear();
        this.clearActiveDragSelections();

        this.touchStartTime = Date.now();
        this.touchStartPosition = { x: coords.x, y: coords.y };

        this.dragStartTimeout = setTimeout(() => {
            if (this.dragStartTile === tileElement && !this.isDragging) {
                this.isDragging = true;
                this.primeDragGeometryCache();
            }
        }, 150);
    }

    /**
     * Cancel drag operation
     */
    cancelDrag() {
        if (this.dragStartTimeout) {
            clearTimeout(this.dragStartTimeout);
            this.dragStartTimeout = null;
        }

        this.isDragging = false;
        this.dragStartTile = null;
        this.invalidateDragGeometryCache();
        this.clearActiveDragSelections();
        this.cancelPendingDragSelectionUpdate();

        if (this.dragPreviewElement) {
            this.dragPreviewElement.style.display = 'none';
        }

        this.draggedTiles.clear();
    }

    /**
     * Reset drag state (for Turbo frame updates)
     */
    resetDragState() {
        if (this.dragStartTimeout) {
            clearTimeout(this.dragStartTimeout);
            this.dragStartTimeout = null;
        }

        this.cancelPendingDragSelectionUpdate();

        this.isDragging = false;
        this.dragStartTile = null;
        this.draggedTiles.clear();
        this.invalidateDragGeometryCache();
        this.clearActiveDragSelections();

        if (this.dragPreviewElement) {
            this.dragPreviewElement.style.display = 'none';
            this.dragPreviewElement = null;
        }
    }

    /**
     * Set up global event listeners for drag functionality
     */
    setup() {
        // Clean up existing setup if already initialized to allow re-initialization
        if (this._setup) {
            this.cleanup();
        }
        this._setup = true;
        // Mouse move handler
        this.mouseMoveHandler = (event) => {
            const coords = this.getEventCoordinates(event);
            if (this.isDragging && this.dragStartTile) {
                this.scheduleDragSelectionUpdate(coords);
            }
        };

        // Touch start handler - detect tile touches and start drag
        this.touchStartHandler = (event) => {
            // Only handle if queue mode is enabled
            if (!this.isQueueModeEnabled()) {
                return;
            }

            // Find the tile element from the touch target
            const tileElement = event.target.closest('[data-controller*="tile"]');
            if (!tileElement) {
                return;
            }

            // Check if tile is already queued
            const tileIdAttr = tileElement.getAttribute('data-tile-tile-id-value');
            if (!tileIdAttr || tileIdAttr === '0') {
                return;
            }

            // Get coordinates and start drag
            const coords = this.getEventCoordinates(event);
            this.startDrag(tileElement, coords);
        };

        // Touch move handler
        this.touchMoveHandler = (event) => {
            const coords = this.getEventCoordinates(event);
            
            // If drag is active, prevent scrolling and update selection
            if (this.isDragging && this.dragStartTile) {
                event.preventDefault();
                this.scheduleDragSelectionUpdate(coords);
                return;
            }
            
            // If drag is pending (during timeout), check for movement threshold
            if (this.dragStartTile && !this.isDragging) {
                const movementDistance = Math.sqrt(
                    Math.pow(coords.x - this.touchStartPosition.x, 2) + 
                    Math.pow(coords.y - this.touchStartPosition.y, 2)
                );
                
                // If movement exceeds threshold (10px), prevent scrolling and activate drag early
                if (movementDistance > 10) {
                    event.preventDefault();
                    // Cancel the timeout and activate drag immediately
                    if (this.dragStartTimeout) {
                        clearTimeout(this.dragStartTimeout);
                        this.dragStartTimeout = null;
                    }
                    this.isDragging = true;
                    this.primeDragGeometryCache();
                    this.scheduleDragSelectionUpdate(coords);
                }
            }
        };

        // Mouse up handler
        this.mouseUpHandler = (event) => {
            if (this.isDragging) {
                this.finalizeDragSelection(event);
            }
        };

        // Touch end handler
        this.touchEndHandler = (event) => {
            if (this.isDragging) {
                this.finalizeDragSelection(event);
            }
        };

        // Touch cancel handler
        this.touchCancelHandler = (event) => {
            if (this.isDragging) {
                this.finalizeDragSelection(event);
            }
        };

        // Escape key handler
        this.keyDownHandler = (event) => {
            if (event.key === 'Escape' && this.isDragging) {
                this.cancelDrag();
            }
        };

        // Add listeners
        document.addEventListener('mousemove', this.mouseMoveHandler);
        document.addEventListener('touchstart', this.touchStartHandler, { passive: true });
        document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
        document.addEventListener('mouseup', this.mouseUpHandler);
        document.addEventListener('touchend', this.touchEndHandler, { passive: true });
        document.addEventListener('touchcancel', this.touchCancelHandler, { passive: true });
        document.addEventListener('keydown', this.keyDownHandler);
    }

    /**
     * Initialize and set up Turbo event listeners (persistent across navigations)
     */
    initialize() {
        // Only initialize once
        if (this._initialized) return;
        this._initialized = true;

        // Set up initial drag handlers
        const setup = () => this.setup();
        
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', setup, { once: true });
        } else {
            setup();
        }

        // Set up Turbo navigation listeners to re-initialize after navigation
        this.turboLoadHandler = () => this.setup();
        this.turboFrameLoadHandler = (e) => {
            if (e.target.id === 'grid-panel') {
                this.setup();
            }
        };
        this.turboBeforeCacheHandler = () => this.cleanup();

        document.addEventListener('turbo:load', this.turboLoadHandler);
        document.addEventListener('turbo:frame-load', this.turboFrameLoadHandler);
        document.addEventListener('turbo:before-cache', this.turboBeforeCacheHandler);
    }

    /**
     * Clean up all event listeners and reset state
     */
    cleanup() {
        // Remove listeners
        if (this.mouseMoveHandler) {
            document.removeEventListener('mousemove', this.mouseMoveHandler);
            this.mouseMoveHandler = null;
        }

        if (this.touchStartHandler) {
            document.removeEventListener('touchstart', this.touchStartHandler);
            this.touchStartHandler = null;
        }

        if (this.touchMoveHandler) {
            document.removeEventListener('touchmove', this.touchMoveHandler);
            this.touchMoveHandler = null;
        }

        if (this.mouseUpHandler) {
            document.removeEventListener('mouseup', this.mouseUpHandler);
            this.mouseUpHandler = null;
        }

        if (this.touchEndHandler) {
            document.removeEventListener('touchend', this.touchEndHandler);
            this.touchEndHandler = null;
        }

        if (this.touchCancelHandler) {
            document.removeEventListener('touchcancel', this.touchCancelHandler);
            this.touchCancelHandler = null;
        }

        if (this.keyDownHandler) {
            document.removeEventListener('keydown', this.keyDownHandler);
            this.keyDownHandler = null;
        }

        // Note: Turbo listeners are NOT removed here - they persist across navigations
        // They are only removed in destroy() if needed

        // Reset state
        this.resetDragState();
        this._setup = false;
    }

    /**
     * Destroy all listeners including Turbo listeners (for complete cleanup)
     */
    destroy() {
        this.cleanup();

        if (this.turboLoadHandler) {
            document.removeEventListener('turbo:load', this.turboLoadHandler);
            this.turboLoadHandler = null;
        }

        if (this.turboFrameLoadHandler) {
            document.removeEventListener('turbo:frame-load', this.turboFrameLoadHandler);
            this.turboFrameLoadHandler = null;
        }

        if (this.turboBeforeCacheHandler) {
            document.removeEventListener('turbo:before-cache', this.turboBeforeCacheHandler);
            this.turboBeforeCacheHandler = null;
        }

        this._initialized = false;
    }
}

// Export singleton instance
export default new DragSelectionManager();
